import * as THREE from "three";
import type { VehicleDef } from "@hyperlight/shared";
import type { GroundVehicle } from "./groundVehicle";

const SCOUT_COLLIDER_RADIUS = 2.15;

/** Rendering boundary for replaceable authored GLTF vehicles. Gameplay only
 * depends on this interface, so swapping the procedural shell changes no sim. */
export interface VehicleView {
  readonly group: THREE.Group;
  update(vehicle: GroundVehicle, dt: number): void;
  /** Render-space positions of the rear exhaust glows, for the boost-trail
   * emitters. Returns how many of `out` were filled. */
  rearExhaustPoints(out: THREE.Vector3[]): number;
  dispose(): void;
}

export class ProceduralSpeederView implements VehicleView {
  readonly group = new THREE.Group();
  private readonly materials: THREE.Material[] = [];
  private readonly geometries: THREE.BufferGeometry[] = [];
  private readonly thrusterGlows: THREE.Mesh[] = [];
  private readonly glow: THREE.MeshBasicMaterial;
  /** Rear exhaust glow — its own material so brightness can track speed/boost
   * without also pulsing the weapon muzzles, which share `glow`. */
  private readonly thruster: THREE.MeshBasicMaterial;
  private readonly body = new THREE.Group();
  private bank = 0;
  private right = new THREE.Vector3();

  constructor(readonly def: VehicleDef, hue = 0.56) {
    this.group.add(this.body);
    const tint = new THREE.Color().setHSL(hue, 0.16, 0.69);
    const hull = new THREE.MeshStandardMaterial({ color: tint, metalness: 0.68, roughness: 0.34 });
    const trim = new THREE.MeshStandardMaterial({ color: 0x151a1e, metalness: 0.88, roughness: 0.23 });
    const accent = new THREE.MeshStandardMaterial({ color: 0xb96724, metalness: 0.7, roughness: 0.32 });
    const saddle = new THREE.MeshStandardMaterial({ color: 0x3a2c24, metalness: 0.22, roughness: 0.72 });
    const glass = new THREE.MeshPhysicalMaterial({ color: 0x365c6c, metalness: 0.1, roughness: 0.12, transparent: true, opacity: 0.7, depthWrite: false });
    this.glow = new THREE.MeshBasicMaterial({ color: 0x56d9ff, transparent: true, opacity: 0.75, toneMapped: false });
    this.thruster = new THREE.MeshBasicMaterial({ color: 0x56d9ff, transparent: true, opacity: 0.6, toneMapped: false });
    this.materials.push(hull, trim, accent, saddle, glass, this.glow, this.thruster);

    const add = (geometry: THREE.BufferGeometry, material: THREE.Material, position: [number, number, number], scale?: [number, number, number]): THREE.Mesh => {
      this.geometries.push(geometry);
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(...position);
      if (scale) mesh.scale.set(...scale);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      this.body.add(mesh);
      return mesh;
    };

    const beam = (a: THREE.Vector3, b: THREE.Vector3, radius: number, material: THREE.Material): THREE.Mesh => {
      const delta = b.clone().sub(a);
      const mesh = add(new THREE.CylinderGeometry(radius, radius, delta.length(), 8), material, [0, 0, 0]);
      mesh.position.copy(a).add(b).multiplyScalar(0.5);
      mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), delta.normalize());
      return mesh;
    };

    // Reference-inspired silhouette: two long armored engine pods flank a
    // narrow central cockpit, leaving the rider fully exposed above them.
    add(new THREE.BoxGeometry(0.68, 0.3, 3.65), trim, [0, -0.15, -0.05]);
    add(new THREE.BoxGeometry(1.92, 0.16, 0.5), trim, [0, -0.02, -1.05]);
    add(new THREE.BoxGeometry(2.12, 0.2, 0.55), hull, [0, 0.0, -0.28]);

    for (const side of [-1, 1]) {
      // Engine pods sit closer to the centreline than the reference so the two
      // exhaust cylinders read as a tight cluster rather than a wide stance.
      const x = side * 0.88;
      const pod = add(new THREE.BoxGeometry(0.82, 0.62, 2.35), trim, [x, -0.05, -1.65]);
      pod.rotation.z = side * -0.025;
      add(new THREE.BoxGeometry(0.7, 0.3, 2.05), hull, [x, 0.25, -1.69]);
      add(new THREE.BoxGeometry(0.18, 0.42, 1.2), hull, [x + side * 0.43, 0.05, -1.71]).rotation.z = side * -0.12;
      add(new THREE.BoxGeometry(0.58, 0.12, 0.72), accent, [x, 0.42, -1.55]).rotation.y = side * 0.04;
      add(new THREE.BoxGeometry(0.12, 0.36, 0.52), accent, [x + side * 0.4, 0.02, -0.77]);

      // Exhaust ring + booster glow are concentric on the pod centreline, so the
      // booster sits dead-centre in its cylinder; the boost trails follow it.
      const ring = add(new THREE.CylinderGeometry(0.4, 0.43, 0.3, 10), trim, [x, -0.04, -0.45]);
      ring.rotation.x = Math.PI / 2;
      const thrust = add(new THREE.CylinderGeometry(0.26, 0.34, 0.18, 12), this.thruster, [x, -0.04, -0.27]);
      thrust.rotation.x = Math.PI / 2;
      this.thrusterGlows.push(thrust);
    }

    // Blunt front with raised cockpit armor (front spikes removed).
    add(new THREE.BoxGeometry(0.62, 0.36, 1.38), hull, [0, 0.28, -0.72]).rotation.x = -0.1;
    add(new THREE.BoxGeometry(0.48, 0.12, 1.22), accent, [0, 0.5, -0.75]).rotation.x = -0.1;
    // Small framed windshield: a trim surround with a tinted glass pane set
    // just in front of it, so it reads as a distinct little window.
    const windFrame = add(new THREE.BoxGeometry(0.52, 0.44, 0.05), trim, [0, 0.68, -0.26]);
    windFrame.rotation.x = -0.46;
    const windscreen = add(new THREE.BoxGeometry(0.42, 0.34, 0.05), glass, [0, 0.686, -0.29]);
    windscreen.rotation.x = -0.46;

    // Low saddle/backstop; the rider root and IK sockets are authored against
    // the visible grips and pegs below.
    add(new THREE.BoxGeometry(0.62, 0.18, 0.88), saddle, [0, 0.48, 0.72]).rotation.x = 0.06;
    add(new THREE.BoxGeometry(0.62, 0.62, 0.18), saddle, [0, 0.76, 1.05]).rotation.x = -0.18;
    add(new THREE.BoxGeometry(0.78, 0.16, 0.32), trim, [0, 0.38, 1.12]);

    beam(new THREE.Vector3(-0.18, 0.5, -0.2), new THREE.Vector3(-0.48, 0.94, -0.39), 0.055, trim);
    beam(new THREE.Vector3(0.18, 0.5, -0.2), new THREE.Vector3(0.48, 0.94, -0.39), 0.055, trim);
    beam(new THREE.Vector3(-0.66, 0.99, -0.4), new THREE.Vector3(0.66, 0.99, -0.4), 0.05, trim);
    beam(new THREE.Vector3(-0.68, 0.99, -0.4), new THREE.Vector3(-0.48, 0.99, -0.4), 0.075, saddle);
    beam(new THREE.Vector3(0.48, 0.99, -0.4), new THREE.Vector3(0.68, 0.99, -0.4), 0.075, saddle);
    beam(new THREE.Vector3(-0.72, 0.08, 0.6), new THREE.Vector3(0.72, 0.08, 0.6), 0.045, trim);

    // Compact rear power unit and central blue exhaust complete the reference's
    // broad-front / pinched-cockpit / glowing-tail read.
    add(new THREE.BoxGeometry(1.0, 0.52, 0.7), trim, [0, 0.18, 1.43]);
    add(new THREE.BoxGeometry(0.68, 0.25, 0.5), hull, [0, 0.52, 1.38]);
    add(new THREE.BoxGeometry(0.42, 0.18, 0.46), accent, [0, 0.7, 1.38]);
    const rearRing = add(new THREE.CylinderGeometry(0.34, 0.4, 0.32, 12), trim, [0, 0.16, 1.82]);
    rearRing.rotation.x = Math.PI / 2;
    const rearGlow = add(new THREE.CylinderGeometry(0.2, 0.29, 0.19, 12), this.thruster, [0, 0.16, 2.02]);
    rearGlow.rotation.x = Math.PI / 2;
    this.thrusterGlows.push(rearGlow);

    for (const hardpoint of def.hardpoints) {
      if (hardpoint.kind !== "weapon") continue;
      const cannon = add(new THREE.CylinderGeometry(0.075, 0.11, 0.72, 8), trim, [...hardpoint.position]);
      cannon.rotation.x = Math.PI / 2;
      const muzzle = add(new THREE.CylinderGeometry(0.08, 0.08, 0.06, 8), this.glow, [
        hardpoint.position[0],
        hardpoint.position[1],
        hardpoint.position[2] - 0.39,
      ]);
      muzzle.rotation.x = Math.PI / 2;
    }
    const size = def.collider.radius / SCOUT_COLLIDER_RADIUS;
    this.body.scale.set(size, Math.max(0.6, def.collider.halfHeight / 0.8), size);
    this.group.name = `vehicle:${def.id}`;
  }

  update(vehicle: GroundVehicle, dt: number): void {
    // Ground-slide lean only — see GlbVehicleView.update: airborne, the flight
    // controller banks the real chassis, and the leftover sideslip decays far
    // too slowly to drive a cosmetic roll without it looking stuck off-level.
    const side = vehicle.gliding ? 0 : vehicle.vel.dot(this.right.set(1, 0, 0).applyQuaternion(vehicle.quat));
    const targetBank = Math.max(-0.18, Math.min(0.18, -side * 0.015));
    this.bank += (targetBank - this.bank) * (1 - Math.exp(-7 * dt));
    this.body.rotation.z = this.bank;
    // Rear thrusters read as a speedometer that BLAZES on boost: a dim idle
    // baseline that brightens with speed, plus a hard spike under boost — the
    // same speed-baseline + boost-spike shape the engine audio uses. Only the
    // brightness changes; the nozzles hold a constant SIZE so they don't visibly
    // swell in and out during a run.
    const speedK = Math.min(1, Math.abs(vehicle.forwardSpeed) / Math.max(1, vehicle.def.handling.maxSpeed));
    this.thruster.opacity = Math.min(1, 0.22 + speedK * 0.4 + vehicle.boostK * 0.5);
  }

  /** Render-space positions of the rear exhaust glows, for the boost-trail
   * emitters. Valid after the group's transform is set for the frame;
   * getWorldPosition recomputes the chain from current local transforms. */
  rearExhaustPoints(out: THREE.Vector3[]): number {
    for (let i = 0; i < this.thrusterGlows.length; i++) {
      if (!out[i]) out[i] = new THREE.Vector3();
      this.thrusterGlows[i].getWorldPosition(out[i]);
    }
    return this.thrusterGlows.length;
  }

  dispose(): void {
    this.group.removeFromParent();
    for (const g of this.geometries) g.dispose();
    for (const m of this.materials) m.dispose();
  }
}
